/**
 * 持仓云同步（方案 B）：服务端密文为正本，本地为缓存。
 * - 密码：复用 holding-auto-backup 保险箱，会话内内存缓存
 * - 密文：复用 /api/holding-backup（web_user_*）
 * - 冲突：后写覆盖（LWW），以云端 updatedAt 为版本
 * - 推送前若云端 updatedAt 新于本地 localEditedAt：改拉不推，避免陈旧 dirty 盖云
 * - 推送中若本地又有变更：保持 dirty 并自动再推，避免清 dirty 丢更新
 */
import { getHoldings } from './assetHoldingStore';
import {
  applyCloudBackupPayload,
  fetchWebUserBackupStatus,
  previewWebUserBackupRestore,
  uploadWebUserBackup
} from './assetHoldingBackupCloud';
import {
  disableAutoBackup,
  enableAutoBackup,
  fetchAutoBackupPassword,
  fetchAutoBackupStatus,
  getAutoBackupLocalState,
  markAutoBackupPromptDismissed
} from './assetAutoBackup';

import {
  AUTO_BACKUP_LOCAL_BASE_KEY,
  SYNC_META_BASE_KEY,
  getAssetStorageUserId,
  scopedAssetKey
} from './assetStorageScope';

const PUSH_DEBOUNCE_MS = 2000;
const PUSH_RETRY_MS = 800;

let sessionPassword = null;
let sessionPasswordUserId = null;
let pushTimer = null;
let syncInFlight = false;
let suppressNotify = false;
let listenerInstalled = false;
let listeners = new Set();

function syncMetaKey() {
  return scopedAssetKey(SYNC_META_BASE_KEY);
}

function readMeta() {
  try {
    const raw = localStorage.getItem(syncMetaKey());
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (e) {
    return {};
  }
}

function writeMeta(patch) {
  const next = { ...readMeta(), ...(patch || {}) };
  try {
    localStorage.setItem(syncMetaKey(), JSON.stringify(next));
  } catch (e) {
    // ignore
  }
  return next;
}

function emit(status) {
  listeners.forEach((fn) => {
    try { fn(status); } catch (e) { /* ignore */ }
  });
}

export function subscribeCloudSyncStatus(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getCloudSyncMeta() {
  return readMeta();
}

export function clearSessionSyncPassword() {
  sessionPassword = null;
  sessionPasswordUserId = null;
}

/** 换号时调用：清内存密码、取消排队推送，避免把 A 的脏数据推到 B */
export function resetCloudSyncRuntimeForUserSwitch() {
  clearSessionSyncPassword();
  if (pushTimer) {
    clearTimeout(pushTimer);
    pushTimer = null;
  }
  syncInFlight = false;
  suppressNotify = false;
}

export async function ensureSessionSyncPassword() {
  const uid = getAssetStorageUserId();
  if (sessionPassword && sessionPasswordUserId && sessionPasswordUserId === uid) {
    return { ok: true, password: sessionPassword };
  }
  sessionPassword = null;
  sessionPasswordUserId = null;
  const res = await fetchAutoBackupPassword();
  if (!res.ok || !res.data?.password) {
    return { ok: false, message: res.message || '无法获取同步密码', code: res.code };
  }
  sessionPassword = res.data.password;
  sessionPasswordUserId = uid;
  return { ok: true, password: sessionPassword };
}

export async function fetchCloudSyncStatus() {
  return fetchAutoBackupStatus();
}

/** 开启云同步：保存密码到服务器保险箱，并立刻把本地推到云端 */
export async function enableCloudSync(password) {
  const en = await enableAutoBackup(password);
  if (!en.ok) return en;
  sessionPassword = password;
  sessionPasswordUserId = getAssetStorageUserId();
  const up = await pushHoldingsToCloud({ force: true, password });
  if (!up.ok) {
    return {
      ok: false,
      message: up.message || '已开启但首次上传失败，请点「立即同步」重试',
      partial: true
    };
  }
  // pushHoldingsToCloud 已按 localEditedAt 处理 dirty
  writeMeta({
    enabledHint: true,
    lastSyncedAt: new Date().toISOString(),
    lastError: ''
  });
  emit({ phase: 'idle', message: up.message });
  return { ok: true, message: '云同步已开启，持仓将跟随登录账号', ...up };
}

export async function disableCloudSync() {
  clearSessionSyncPassword();
  if (pushTimer) {
    clearTimeout(pushTimer);
    pushTimer = null;
  }
  const res = await disableAutoBackup();
  writeMeta({ enabledHint: false, dirty: false, lastError: '' });
  emit({ phase: 'idle', message: '已关闭云同步' });
  return res;
}

export function markCloudSyncPromptDismissed() {
  return markAutoBackupPromptDismissed();
}

export function shouldShowCloudSyncPrompt(holdingsLen) {
  const local = getAutoBackupLocalState();
  return holdingsLen > 0 && !local.promptDismissed;
}

/**
 * 本地持仓变更后调用（由 saveHoldings → asset-holdings-changed 触发）
 */
export function notifyHoldingsChangedForCloudSync() {
  if (suppressNotify) return;
  if (!getAssetStorageUserId()) return; // 未登录不推云、不标 dirty
  writeMeta({ dirty: true, localEditedAt: new Date().toISOString() });
  schedulePushToCloud();
}

/** 全局监听本地持仓写入（App 级安装一次即可覆盖交易页等所有入口） */
export function installHoldingsChangeListener() {
  if (typeof window === 'undefined') return () => {};
  if (listenerInstalled) return () => {};
  listenerInstalled = true;
  const handler = () => notifyHoldingsChangedForCloudSync();
  window.addEventListener('asset-holdings-changed', handler);
  return () => {
    window.removeEventListener('asset-holdings-changed', handler);
    listenerInstalled = false;
  };
}

function schedulePushToCloud(delayMs) {
  const delay = Number.isFinite(Number(delayMs)) ? Math.max(0, Number(delayMs)) : PUSH_DEBOUNCE_MS;
  if (pushTimer) clearTimeout(pushTimer);
  pushTimer = setTimeout(() => {
    pushTimer = null;
    void pushHoldingsToCloud({ reason: 'debounce' });
  }, delay);
}

function markDailyBackupLocalSuccess() {
  try {
    const today = new Date().toISOString().slice(0, 10);
    const prev = getAutoBackupLocalState();
    localStorage.setItem(scopedAssetKey(AUTO_BACKUP_LOCAL_BASE_KEY), JSON.stringify({
      ...prev,
      lastSuccessYmd: today,
      lastSuccessAt: new Date().toISOString(),
      lastError: ''
    }));
  } catch (e) { /* ignore */ }
}

/** ISO 时间戳比较：a 是否严格新于 b；b 缺失时视为 a 更新 */
function isTimestampNewer(a, b) {
  const sa = a ? String(a) : '';
  const sb = b ? String(b) : '';
  if (!sa) return false;
  if (!sb) return true;
  const ta = Date.parse(sa);
  const tb = Date.parse(sb);
  if (Number.isFinite(ta) && Number.isFinite(tb)) return ta > tb;
  return sa > sb;
}

/**
 * 本地 dirty 但云端版本更新：应拉云而非推本地（防陈旧端覆盖）。
 * localEditedAt 缺失时宁可拉云，避免未知年龄的 dirty 盖掉云端。
 */
function shouldPullInsteadOfDirtyPush(cloudUpdatedAt, localEditedAt) {
  return isTimestampNewer(cloudUpdatedAt, localEditedAt || '');
}

export async function pushHoldingsToCloud(opts = {}) {
  const force = Boolean(opts.force);
  const headersToken = localStorage.getItem('auth_token');
  if (!headersToken || !getAssetStorageUserId()) {
    return { ok: false, skipped: true, reason: 'not_logged_in', message: '未登录' };
  }

  const st = await fetchAutoBackupStatus();
  if (!st.ok) return { ...st, skipped: true, reason: 'status_failed' };
  if (!st.data?.enabled || !st.data?.hasPassword) {
    return { ok: true, skipped: true, reason: 'disabled', message: '未开启云同步' };
  }

  const meta = readMeta();
  if (!force && !meta.dirty) {
    return { ok: true, skipped: true, reason: 'clean', message: '本地无变更' };
  }

  let password = opts.password || null;
  if (!password) {
    const pwd = await ensureSessionSyncPassword();
    if (!pwd.ok) {
      writeMeta({ lastError: pwd.message || '无法获取同步密码' });
      emit({ phase: 'error', message: pwd.message || '无法获取同步密码' });
      return { ...pwd, skipped: false, reason: 'password_failed' };
    }
    password = pwd.password;
  }

  // 推前再看一眼云端版本：旧 dirty 不得盖新云（手动「强制用本地覆盖」可设 allowOverwriteNewerCloud）
  if (!opts.allowOverwriteNewerCloud) {
    const cloud = await fetchWebUserBackupStatus();
    if (cloud.ok && cloud.hasBackup && cloud.updatedAt) {
      const localEditedAt = String(readMeta().localEditedAt || '');
      if (shouldPullInsteadOfDirtyPush(cloud.updatedAt, localEditedAt)) {
        emit({ phase: 'pull', message: '云端更新，改为拉取…' });
        const pulled = await pullHoldingsFromCloud({ force: true, password });
        return {
          ...pulled,
          action: 'pull_instead_of_stale_push',
          message: pulled.message || '云端较新，已拉取而非上传本地旧数据'
        };
      }
    }
  }

  if (syncInFlight && !force) {
    // 当前推送结束后再试，避免 in_flight 直接丢预约
    schedulePushToCloud(PUSH_RETRY_MS);
    return { ok: true, skipped: true, reason: 'in_flight', message: '同步进行中，稍后重试' };
  }

  // 记录本次推送开始时的本地编辑戳；上传期间若再次变更则不能清 dirty
  const editedAtAtStart = String(readMeta().localEditedAt || '');
  syncInFlight = true;
  emit({ phase: 'push', message: '正在同步到云端…' });
  try {
    const up = await uploadWebUserBackup(password);
    if (!up.ok) {
      writeMeta({ lastError: up.message || '上传失败' });
      emit({ phase: 'error', message: up.message || '上传失败' });
      // 失败保留 dirty，稍后自动再试
      schedulePushToCloud(PUSH_DEBOUNCE_MS * 2);
      return { ...up, skipped: false, reason: 'upload_failed' };
    }

    const editedAtNow = String(readMeta().localEditedAt || '');
    const editedDuringPush = editedAtNow && editedAtNow !== editedAtAtStart;
    if (editedDuringPush) {
      writeMeta({
        dirty: true,
        lastAppliedCloudAt: up.updatedAt || new Date().toISOString(),
        lastSyncedAt: new Date().toISOString(),
        lastError: '',
        lastHoldingCount: up.holdingCount ?? null
      });
      markDailyBackupLocalSuccess();
      schedulePushToCloud(PUSH_RETRY_MS);
      emit({ phase: 'push', message: '本地又有变更，继续同步…' });
      return {
        ok: true,
        skipped: false,
        pendingMore: true,
        message: '已同步一版，检测到新变更将继续上传',
        holdingCount: up.holdingCount,
        updatedAt: up.updatedAt
      };
    }

    writeMeta({
      dirty: false,
      lastAppliedCloudAt: up.updatedAt || new Date().toISOString(),
      lastSyncedAt: new Date().toISOString(),
      lastError: '',
      lastHoldingCount: up.holdingCount ?? null
    });
    markDailyBackupLocalSuccess();
    emit({ phase: 'idle', message: up.message || '已同步到云端' });
    return {
      ok: true,
      skipped: false,
      message: up.message || '已同步到云端',
      holdingCount: up.holdingCount,
      updatedAt: up.updatedAt
    };
  } finally {
    syncInFlight = false;
  }
}

export async function pullHoldingsFromCloud(opts = {}) {
  const password = opts.password
    || (await ensureSessionSyncPassword()).password;
  if (!password) {
    return { ok: false, message: '无法获取同步密码' };
  }

  // 本地 dirty：仅当本地编辑不早于云端时才改推；否则继续拉，避免陈旧端盖云
  if (!opts.force && readMeta().dirty) {
    const cloud = await fetchWebUserBackupStatus();
    const localEditedAt = String(readMeta().localEditedAt || '');
    const cloudNewer = cloud.ok && cloud.hasBackup && cloud.updatedAt
      && shouldPullInsteadOfDirtyPush(cloud.updatedAt, localEditedAt);
    if (!cloudNewer) {
      const up = await pushHoldingsToCloud({ force: true, password });
      return { ...up, action: 'push_instead_of_pull' };
    }
  }

  emit({ phase: 'pull', message: '正在从云端拉取持仓…' });
  const preview = await previewWebUserBackupRestore(password);
  if (!preview.ok) {
    emit({ phase: 'error', message: preview.message });
    return preview;
  }

  // 解密等待期间若本地又写入：同样先比版本再决定推/拉
  if (!opts.force && readMeta().dirty) {
    const localEditedAt = String(readMeta().localEditedAt || '');
    const previewAt = preview.updatedAt ? String(preview.updatedAt) : '';
    if (!shouldPullInsteadOfDirtyPush(previewAt, localEditedAt)) {
      const up = await pushHoldingsToCloud({ force: true, password });
      return { ...up, action: 'push_instead_of_pull' };
    }
  }

  suppressNotify = true;
  try {
    const applied = applyCloudBackupPayload(preview.payload);
    if (!applied.ok) {
      emit({ phase: 'error', message: applied.message });
      return applied;
    }
    writeMeta({
      dirty: false,
      lastAppliedCloudAt: preview.updatedAt || new Date().toISOString(),
      lastSyncedAt: new Date().toISOString(),
      lastError: '',
      lastHoldingCount: applied.holdingCount ?? preview.holdingCount ?? null
    });
    emit({ phase: 'idle', message: applied.message || '已从云端同步持仓' });
    return {
      ok: true,
      holdingCount: applied.holdingCount,
      updatedAt: preview.updatedAt,
      message: applied.message || '已从云端同步持仓',
      applied: true
    };
  } finally {
    suppressNotify = false;
  }
}

/**
 * 进入资产中心时引导同步：
 * - 本地 dirty 且不早于云端 → 推送
 * - 本地 dirty 但云端更新 → 拉取（防陈旧 dirty 盖云）
 * - 否则若云端有更新 → 拉取覆盖本地
 * - 云空本地有 → 推送
 */
export async function bootstrapCloudSync() {
  const token = localStorage.getItem('auth_token');
  if (!token || !getAssetStorageUserId()) {
    return { ok: false, skipped: true, reason: 'not_logged_in' };
  }

  const st = await fetchAutoBackupStatus();
  if (!st.ok) return { ...st, skipped: true, reason: 'status_failed' };
  if (!st.data?.enabled || !st.data?.hasPassword) {
    return { ok: true, skipped: true, reason: 'disabled', enabled: false, username: st.data?.username };
  }

  const pwd = await ensureSessionSyncPassword();
  if (!pwd.ok) {
    return { ...pwd, skipped: false, reason: 'password_failed', enabled: true };
  }

  const cloud = await fetchWebUserBackupStatus();
  if (!cloud.ok) {
    return { ...cloud, skipped: true, reason: 'cloud_status_failed', enabled: true };
  }

  const meta = readMeta();
  const localHoldings = getHoldings();
  const localCount = Array.isArray(localHoldings) ? localHoldings.length : 0;
  const cloudAt = cloud.updatedAt ? String(cloud.updatedAt) : '';
  const lastApplied = meta.lastAppliedCloudAt ? String(meta.lastAppliedCloudAt) : '';
  const localEditedAt = meta.localEditedAt ? String(meta.localEditedAt) : '';

  // 本地已空但云端有备份：一律拉取，避免换号/误覆盖后误判「已一致」而不恢复
  // 且绝不能在本地空时因 dirty 把空包推上去盖掉云端
  if (localCount === 0 && cloud.hasBackup) {
    const pulled = await pullHoldingsFromCloud({ force: true, password: pwd.password });
    writeMeta({ sessionBootstrapped: true });
    return { ...pulled, enabled: true, action: pulled.action || 'pull' };
  }

  if (meta.dirty) {
    if (cloud.hasBackup && cloudAt && shouldPullInsteadOfDirtyPush(cloudAt, localEditedAt)) {
      const pulled = await pullHoldingsFromCloud({ force: true, password: pwd.password });
      writeMeta({ sessionBootstrapped: true });
      return { ...pulled, enabled: true, action: 'pull_cloud_newer' };
    }
    const up = await pushHoldingsToCloud({ force: true, password: pwd.password });
    return { ...up, enabled: true, action: up.action || 'push' };
  }

  if (cloud.hasBackup && cloudAt && cloudAt !== lastApplied) {
    const pulled = await pullHoldingsFromCloud({ password: pwd.password });
    return { ...pulled, enabled: true, action: pulled.action || 'pull' };
  }

  if (!cloud.hasBackup && localCount > 0) {
    const up = await pushHoldingsToCloud({ force: true, password: pwd.password });
    return { ...up, enabled: true, action: 'push' };
  }

  // 首次本机会话：即使时间戳相同也拉一次，保证多端一致
  if (cloud.hasBackup && !meta.sessionBootstrapped) {
    const pulled = await pullHoldingsFromCloud({ password: pwd.password });
    writeMeta({ sessionBootstrapped: true });
    return { ...pulled, enabled: true, action: pulled.action || 'pull' };
  }

  writeMeta({ sessionBootstrapped: true });
  return {
    ok: true,
    skipped: true,
    reason: 'uptodate',
    enabled: true,
    message: '持仓已与云端一致',
    updatedAt: cloudAt || lastApplied
  };
}

// re-export helpers used by UI
export { fetchAutoBackupStatus as fetchSyncEnabledStatus };
